/**
 * A fixture `AdminSource`, so `/__ui/admin` renders with **no session, no api and no
 * database** and a served build can be hit-tested against the captures (the trick B4, M5 and
 * M6-A2 all use).
 *
 * The shape of the data is the reference account's, scaled down: four admins, a handful of
 * members, guests including a tombstone and the non-human agent seat, and the single MGMT team
 * with 6 members of whom 5 are owners (`discovery/users.md` §5 / §6).
 */
import { semanticColors } from '@elizmonday/ui';
import type {
  ActiveImpersonation,
  AdminSource,
  AuditQuery,
  AuditRow,
  BoardPermissionsSummary,
  DirectoryAdminColumns,
  DirectoryPage,
  DirectoryPersonRow,
  DirectoryUserRow,
  InvitationRow,
  PublicAccount,
  TeamSummary,
  TokenOverviewRow,
} from './types';

const iso = (days: number): string => new Date(Date.UTC(2026, 8, 10) - days * 86_400_000).toISOString();

const USERS: Array<DirectoryPersonRow & DirectoryAdminColumns> = [
  row('1', 'Nico Nuzzi', 'nico@example.com', 'admin', 'active', { title: 'CEO', joined: 3000, platform: 'admin' }),
  row('2', 'Roberto', 'roberto@example.com', 'admin', 'active', {
    title: 'CTO',
    joined: 2900,
    invitedBy: 'Matteo',
    platform: 'roberto',
  }),
  row('3', 'Angela', 'angela@example.com', 'admin', 'active', { joined: 2000, teams: ['MGMT'] }),
  row('4', 'Mike', 'mike@example.com', 'admin', 'active', { title: 'Marketing Manager', joined: 2600, teams: ['MGMT'] }),
  row('5', 'Fedor', 'fedor@example.com', 'member', 'active', { joined: 1200 }),
  row('6', 'Alex', 'alex@example.com', 'member', 'active', { joined: 2400, teams: ['MGMT'] }),
  row('7', 'Atea', 'atea@example.com', 'member', 'inactive', { joined: 1800, teams: ['MGMT'], deactivated: true }),
  row('8', 'Sagar', 'sagar@example.com', 'guest', 'active', { joined: 700 }),
  row('9', 'Matylda Wierietielny', 'matylda@example.com', 'guest', 'pending', { joined: null }),
  row('10', 'Kasia', 'kasia@deleted.user', 'guest', 'inactive', { joined: 2100, deleted: true }),
  row('11', 'Assistant', 'agent@agent.example.com', 'member', 'active', { kind: 'personal_agent_member' }),
  // An **active** guest who is not on the sandbox board: the case the share dialog must refuse
  // on a `public` board (P1 acceptance B5).
  row('12', 'Michael White', 'michael@example.com', 'guest', 'active', { joined: 400 }),
];

function row(
  id: string,
  name: string,
  email: string,
  role: DirectoryUserRow['role'],
  status: DirectoryUserRow['status'],
  extra: {
    title?: string;
    joined?: number | null;
    invitedBy?: string;
    teams?: string[];
    deleted?: boolean;
    deactivated?: boolean;
    kind?: string;
    /** M0-F3b §8.3: the platform username this person is linked as. */
    platform?: string;
  } = {},
): DirectoryPersonRow & DirectoryAdminColumns {
  return {
    id,
    name,
    email,
    role,
    product: 'monday',
    status,
    kind: extra.kind ?? role,
    teams: (extra.teams ?? []).map((teamName) => ({ id: '555487', name: teamName })),
    joinedAt: extra.joined === null || extra.joined === undefined ? null : iso(extra.joined),
    invitedBy: extra.invitedBy ? { id: '99', name: extra.invitedBy } : null,
    lastActivityAt: status === 'active' ? iso(1) : null,
    twoFactor: 'disabled',
    isDeleted: extra.deleted === true,
    deactivatedAt: extra.deactivated === true ? iso(30) : null,
    deactivatedBy: extra.deactivated === true ? { id: '2', name: 'Roberto' } : null,
    isNonHuman: extra.kind === 'personal_agent_member',
    platform: extra.platform
      ? { subject: extra.platform, linkedAt: iso(20), lastLoginAt: iso(1) }
      : null,
  };
}

const ACCOUNT: PublicAccount = {
  name: 'Eli Z Group',
  productName: 'Eli Z Monday',
  wordmark: 'Eli Z Monday',
  logoUrl: null,
  emailHeaderUrl: null,
  brand: {
    primary: semanticColors.primary,
    hover: semanticColors.primaryHover,
    onPrimary: semanticColors.primaryBackground,
  },
  homePage: { kind: 'default', targetId: null },
  firstDayOfTheWeek: 'monday',
  weekendDays: 'saturday-sunday',
  showWeekends: true,
  defaultWorkspace: { id: '9960567', name: 'Main workspace' },
};

const BOARD: BoardPermissionsSummary = {
  boardId: '1000000000000',
  boardName: 'Sandbox – Column types',
  kind: 'share',
  permissions: 'everyone',
  level: {
    value: 'everyone',
    label: 'Edit everything',
    description: 'Board members can edit everything on this board, including its structure.',
    known: true,
  },
  principals: [
    principal('user', '2', 'Roberto', 'owner', { title: 'CTO' }),
    principal('user', '5', 'Fedor', 'subscriber', {}),
    principal('user', '8', 'Sagar', 'subscriber', { isGuest: true }),
    principal('team', '555487', 'MGMT', 'subscriber', {}),
  ],
  ownerCount: 1,
  guestCount: 1,
  viewer: { isOwner: true, canManage: true, canSetPermissions: true },
  restrictedColumns: [],
};

function principal(
  kind: 'user' | 'team',
  id: string,
  name: string,
  role: 'owner' | 'subscriber',
  extra: { title?: string; isGuest?: boolean },
): BoardPermissionsSummary['principals'][number] {
  return {
    kind,
    id,
    name,
    email: kind === 'user' ? `${name.toLowerCase()}@example.com` : null,
    title: extra.title ?? null,
    photoUrl: null,
    role,
    isGuest: extra.isGuest === true,
    isDeactivated: false,
    isPending: false,
    isDeleted: false,
  };
}

const LEVELS = [
  {
    value: 'everyone',
    label: 'Edit everything',
    description: 'Board members can edit everything on this board, including its structure.',
  },
  {
    value: 'collaborators',
    label: 'Only edit content',
    description: 'Board members can add and edit items, but not columns, groups or views.',
  },
  {
    value: 'assignee',
    label: 'Only edit assigned items',
    description: 'Board members can only edit items they are assigned to in a people column.',
  },
  {
    value: 'owners',
    label: 'View and comment',
    description: 'Board members can read the board and post updates, but change nothing.',
  },
];

/**
 * `canGrantSuperAdmin` mirrors the server field (T-9f): the fifth role is offered only to a
 * superadmin, so the fixture takes it as an argument rather than assuming one.
 */
export function createFixtureSource(
  options: { canGrantSuperAdmin?: boolean; canViewAsUser?: boolean } = {},
): AdminSource {
  /** M8-P3: one open "View as", so `/__ui/admin?screen=security` has a row to draw. */
  let impersonations: ActiveImpersonation[] = [
    {
      sessionId: 'imp-1',
      startedAt: iso(0),
      expiresAt: iso(-1),
      ip: '10.0.0.4',
      impersonator: { id: '1', name: 'Nico Nuzzi' },
      target: { id: '5', name: 'Fedor', email: 'fedor@example.com', role: 'member' },
    },
  ];
  let users = [...USERS];
  let account = { ...ACCOUNT };
  /*
   * Round 10n. The fixture is what `/__ui/admin` and the jsdom suite drive, so every control
   * this round added has to be operable here too: an invitation still inside its 60 s resend
   * window (P-251), teams that can be renamed and deleted (P-247, P-249), tokens that can be
   * revoked (P-245) and an audit log with something to filter and export (P-243, P-244).
   */
  let invitationRows: InvitationRow[] = [
    {
      id: '1',
      email: 'newcomer@example.com',
      role: 'guest',
      invitedBy: { id: '2', name: 'Roberto' },
      createdAt: iso(1),
      lastSentAt: iso(1),
      // Long past: this one may be resent.
      resendAvailableAt: iso(1),
      expiresAt: iso(-13),
      resentCount: 0,
      state: 'sent',
      product: 'monday',
    },
    {
      id: '2',
      email: 'just-invited@example.com',
      role: 'member',
      invitedBy: { id: '2', name: 'Roberto' },
      createdAt: iso(0),
      lastSentAt: new Date().toISOString(),
      // Inside the window: B3's disabled Resend, with its countdown.
      resendAvailableAt: new Date(Date.now() + 60_000).toISOString(),
      expiresAt: iso(-14),
      resentCount: 1,
      state: 'sent',
      product: 'monday',
    },
  ];
  let teamRows: TeamSummary[] = [
    {
      id: '555487',
      name: 'MGMT',
      description: null,
      pictureUrl: null,
      isGuest: false,
      isDeleted: false,
      memberCount: 6,
      ownerCount: 5,
      contentCount: 13,
    },
  ];
  let tokenRows: TokenOverviewRow[] = [
    {
      id: '1',
      owner: { id: '2', name: 'Roberto', email: 'roberto@example.com', role: 'admin', status: 'active' },
      name: 'CI',
      kind: 'personal',
      createdAt: iso(40),
      lastUsedAt: iso(0),
      lastSeenApiVersion: '2026-10',
      lastSeenUserAgent: 'node',
      revokedAt: null,
    },
  ];
  const auditRows: AuditRow[] = [
    {
      id: 'auth:1',
      source: 'auth',
      event: 'user.login_succeeded',
      at: iso(0),
      actor: { id: '2', name: 'Roberto' },
      target: null,
      boardId: null,
      ip: '127.0.0.1',
      data: {},
    },
    {
      id: 'board:2',
      source: 'board',
      event: 'delete_board',
      at: iso(1),
      actor: { id: '1', name: 'Nico Nuzzi' },
      target: null,
      boardId: '1000000000000',
      ip: null,
      data: { board_id: 1000000000000 },
    },
  ];
  const matchesAudit = (row: AuditRow, query: AuditQuery): boolean => {
    if (query.sources && !query.sources.includes(row.source)) return false;
    if (query.events && query.events.length > 0 && !query.events.includes(row.event)) return false;
    if (query.actorId && row.actor?.id !== query.actorId) return false;
    if (query.targetId && row.target?.id !== query.targetId) return false;
    if (query.from && row.at < query.from) return false;
    if (query.to && row.at > query.to) return false;
    return true;
  };
  let board: BoardPermissionsSummary = { ...BOARD, principals: [...BOARD.principals] };

  const page = (query: Parameters<AdminSource['directory']>[0]): DirectoryPage => {
    let rows = users;
    if (query.search) {
      const needle = query.search.toLowerCase();
      rows = rows.filter(
        (user) => user.name.toLowerCase().includes(needle) || user.email.toLowerCase().includes(needle),
      );
    }
    if (query.roles && query.roles.length > 0) rows = rows.filter((user) => query.roles?.includes(user.role));
    if (query.statuses && query.statuses.length > 0) {
      rows = rows.filter((user) => query.statuses?.includes(user.status));
    }
    if (query.noTeam) rows = rows.filter((user) => user.teams.length === 0);
    if (query.teamIds && query.teamIds.length > 0) {
      rows = rows.filter((user) => user.teams.some((team) => query.teamIds?.includes(team.id)));
    }
    return {
      rows,
      total: rows.length,
      nextCursor: null,
      canGrantSuperAdmin: options.canGrantSuperAdmin === true,
      /* The row menu offers "View as <name>" only to a superadmin (T-9u). */
      canViewAsUser: options.canViewAsUser ?? options.canGrantSuperAdmin === true,
      // The fixture renders the console, which is only ever opened by an admin (P-250).
      adminColumns: true,
      facets: {
        roles: count(users, (user) => user.role),
        statuses: count(users, (user) => user.status),
        teams: [{ id: '555487', name: 'MGMT', count: users.filter((u) => u.teams.length > 0).length }],
        noTeam: users.filter((user) => user.teams.length === 0).length,
      },
    };
  };

  return {
    directory: async (query) => page(query),
    user: async (userId) => {
      const row = users.find((user) => user.id === userId);
      if (!row) throw new Error('User not found');
      return { ...row, sessionCount: 2, tokenCount: 1, boardsOwned: 4 };
    },
    setRole: async ({ userIds, role }) => {
      users = users.map((user) => (userIds.includes(user.id) ? { ...user, role } : user));
      return { updated: userIds, errors: [] };
    },
    deactivate: async (userIds) => {
      users = users.map((user) =>
        userIds.includes(user.id) ? { ...user, status: 'inactive', deactivatedAt: iso(0) } : user,
      );
      return { updated: userIds, errors: [] };
    },
    reactivate: async ({ userIds }) => {
      users = users.map((user) =>
        userIds.includes(user.id) ? { ...user, status: 'active', deactivatedAt: null } : user,
      );
      return { updated: userIds, errors: [] };
    },
    resetPassword: async () => ({ ok: true }),
    unlinkPlatform: async () => ({ unlinked: true }),

    startImpersonation: async (userId) => {
      const target = users.find((user) => user.id === userId);
      if (!target) throw new Error('User not found');
      return {
        target: {
          id: target.id,
          name: target.name,
          email: target.email,
          role: target.role,
          status: target.status,
        },
        expiresAt: iso(-1),
      };
    },
    activeImpersonations: async () => impersonations,
    endImpersonation: async (sessionId) => {
      const before = impersonations.length;
      impersonations = impersonations.filter((row) => row.sessionId !== sessionId);
      return { ended: impersonations.length < before };
    },

    invitations: async (tab) =>
      tab === 'pending'
        ? {
            rows: [],
            total: 0,
            emptyReason:
              'Eli Z Monday has no request-to-join queue: an invitation is sent by an admin, and a self-join domain creates the account directly.',
          }
        : { rows: invitationRows, total: invitationRows.length, emptyReason: null },
    invite: async ({ paste }) => ({
      invited: paste
        .split(/[\s,;]+/)
        .filter((value) => value.includes('@'))
        .map((email, index) => ({ email, userId: String(1000 + index) })),
      errors: paste
        .split(/[\s,;]+/)
        .filter((value) => value.length > 0 && !value.includes('@'))
        .map((email) => ({ email, code: 'ERROR', message: 'That does not look like an e-mail address.' })),
    }),
    resendInvite: async (id) => {
      invitationRows = invitationRows.map((row) =>
        row.id === id
          ? {
              ...row,
              lastSentAt: new Date().toISOString(),
              resendAvailableAt: new Date(Date.now() + 60_000).toISOString(),
              resentCount: row.resentCount + 1,
            }
          : row,
      );
      return undefined;
    },
    revokeInvite: async (id) => {
      invitationRows = invitationRows.map((row) =>
        row.id === id ? { ...row, state: 'revoked' as const } : row,
      );
      return undefined;
    },

    teams: async () => teamRows.filter((row) => !row.isDeleted),
    team: async (teamId) => ({
      id: teamId,
      name: teamRows.find((row) => row.id === teamId)?.name ?? 'MGMT',
      description: null,
      pictureUrl: null,
      isGuest: false,
      isDeleted: false,
      memberCount: 6,
      ownerCount: 5,
      contentCount: 13,
      members: [
        { userId: '7', name: 'Atea', email: 'atea@example.com', title: null, photoUrl: null, isOwner: true, role: 'member', status: 'active', isDeleted: false },
        { userId: '3', name: 'Angela', email: 'angela@example.com', title: null, photoUrl: null, isOwner: true, role: 'admin', status: 'active', isDeleted: false },
        { userId: '4', name: 'Mike', email: 'mike@example.com', title: 'Marketing Manager', photoUrl: null, isOwner: true, role: 'admin', status: 'active', isDeleted: false },
        { userId: '2', name: 'Roberto', email: 'roberto@example.com', title: 'CTO', photoUrl: null, isOwner: true, role: 'admin', status: 'active', isDeleted: false },
        { userId: '1', name: 'Nico Nuzzi', email: 'nico@example.com', title: 'CEO', photoUrl: null, isOwner: true, role: 'admin', status: 'active', isDeleted: false },
        { userId: '6', name: 'Alex', email: 'alex@example.com', title: 'Growth Manager', photoUrl: null, isOwner: false, role: 'member', status: 'active', isDeleted: false },
      ],
    }),
    createTeam: async ({ name, memberIds }) => {
      const created: TeamSummary = {
        id: String(555488 + teamRows.length),
        name,
        description: null,
        pictureUrl: null,
        isGuest: false,
        isDeleted: false,
        memberCount: Math.max(1, memberIds.length),
        ownerCount: 1,
        contentCount: 0,
      };
      teamRows = [...teamRows, created];
      return created;
    },
    updateTeam: async ({ teamId, name }) => {
      teamRows = teamRows.map((row) => (row.id === teamId ? { ...row, name } : row));
      return undefined;
    },
    deleteTeam: async (teamId) => {
      teamRows = teamRows.map((row) => (row.id === teamId ? { ...row, isDeleted: true } : row));
      return undefined;
    },
    addTeamMembers: async () => undefined,
    removeTeamMembers: async () => undefined,
    setTeamOwner: async () => undefined,

    account: async () => account,
    updateAccount: async (patch) => {
      const brand = { ...account.brand };
      if (typeof patch['brandPrimary'] === 'string') brand.primary = patch['brandPrimary'];
      if (typeof patch['brandTextOnPrimary'] === 'string') brand.onPrimary = patch['brandTextOnPrimary'];
      account = {
        ...account,
        ...(typeof patch['productName'] === 'string' ? { productName: patch['productName'] } : {}),
        ...(typeof patch['name'] === 'string' ? { name: patch['name'] } : {}),
        ...('wordmarkText' in patch ? { wordmark: (patch['wordmarkText'] as string | null) ?? null } : {}),
        ...('logoUrl' in patch ? { logoUrl: (patch['logoUrl'] as string | null) ?? null } : {}),
        ...(typeof patch['showWeekends'] === 'boolean' ? { showWeekends: patch['showWeekends'] } : {}),
        ...(typeof patch['weekendDays'] === 'string' ? { weekendDays: patch['weekendDays'] } : {}),
        brand,
      };
      return account;
    },
    security: async () => ({
      passwordMinLength: 12,
      passwordMaxLength: 256,
      passwordRejectCommon: true,
      passwordRejectEmailSubstring: true,
      sessionIdleDays: 14,
      sessionAbsoluteDays: 90,
      deviceVerificationEnabled: false,
      autoJoinDomains: [],
      defaultNewUserRole: 'member',
      inviteRequiresAdmin: false,
      invitationTtlDays: 14,
      sso: {
        enabled: true,
        allowPasswordLogin: true,
        autoProvision: true,
        configured: true,
        label: 'Sign in with Eli Z Platform',
      },
      planned: [
        { feature: 'Two-factor authentication', milestone: 'M8.P3' },
        { feature: 'SCIM provisioning', milestone: 'not planned' },
        { feature: 'IP address restriction', milestone: 'not planned' },
      ],
    }),
    updateSecurity: async () => (await createFixtureSource().security()),
    capabilities: async () => ({
      cells: [
        { action: 'account.admin', role: 'admin', fallback: true, allowed: true, configurable: false },
        { action: 'account.admin', role: 'member', fallback: false, allowed: false, configurable: false },
        { action: 'account.users.invite', role: 'member', fallback: true, allowed: true, configurable: true },
        { action: 'account.teams.manage', role: 'member', fallback: false, allowed: false, configurable: true },
        { action: 'board.create', role: 'member', fallback: true, allowed: true, configurable: true },
        { action: 'workspace.create', role: 'member', fallback: true, allowed: true, configurable: true },
        { action: 'api_token.create_self', role: 'guest', fallback: false, allowed: false, configurable: false },
        { action: 'search.global', role: 'admin', fallback: true, allowed: true, configurable: false },
      ],
    }),
    setCapability: async () => (await createFixtureSource().capabilities()),
    /** P-246 — the Sessions card's danger button. */
    logoutEveryone: async () => ({ revoked: 7, people: 4 }),

    usage: async (days) => ({
      windowDays: days,
      summary: { boardsUpdated: 34, peoplePosted: 9, updates: 121 },
      storage: { usedBytes: 3 * 1024 ** 3, quotaBytes: null, byType: [{ type: 'image/png', bytes: 2 * 1024 ** 3 }] },
      people: { joined: 2, contributed: 11, invitedNeverJoined: 1 },
      boardsCreated: { main: 3, shared: 7, private: 1 },
      seats: { members: 9, guests: 11, viewers: 0, pending: 33, admins: 4 },
    }),
    audit: async (input) => ({
      rows: auditRows.filter((row) => matchesAudit(row, input)),
      nextCursor: null,
    }),
    exportAudit: async (input) => ({
      csv: [
        'When,Source,Event,Actor,Target,Board,IP,Details',
        ...auditRows
          .filter((row) => matchesAudit(row, input))
          .map((row) =>
            [row.at, row.source, row.event, row.actor?.name ?? '', '', row.boardId ?? '', row.ip ?? '', ''].join(','),
          ),
      ].join('\n'),
    }),
    tokens: async () => ({ rows: tokenRows }),
    revokeToken: async (id) => {
      tokenRows = tokenRows.map((row) =>
        row.id === id ? { ...row, revokedAt: new Date().toISOString() } : row,
      );
      return { ok: true as const };
    },

    levels: async () => LEVELS,
    boardSummary: async () => board,
    setBoardPermissions: async ({ permissions }) => {
      const level = LEVELS.find((entry) => entry.value === permissions) ?? LEVELS[0]!;
      board = { ...board, permissions, level: { ...level, known: true } };
      return undefined;
    },
    myBoardPermissions: async () => [
      {
        category: 'Items',
        rows: [
          { action: 'item.create', allowed: true },
          { action: 'item.update', allowed: true },
          { action: 'item.delete', allowed: true },
        ],
      },
      {
        category: 'General',
        rows: [
          { action: 'board.read', allowed: true },
          { action: 'board.permissions.update', allowed: true },
        ],
      },
      { category: 'Updates', rows: [{ action: 'update.create', allowed: true }] },
      { category: 'Columns', rows: [{ action: 'column.restrict', allowed: true }] },
      { category: 'Groups', rows: [{ action: 'group.create', allowed: true }] },
      { category: 'Views', rows: [{ action: 'view.create', allowed: true }] },
    ],
    whoLosesAccess: async ({ nextKind }) =>
      nextKind === 'public'
        ? { guests: ['Sagar'], members: [], total: 1 }
        : { guests: [], members: ['Fedor'], total: 1 },
    people: async ({ query }) =>
      users
        .filter((user) => user.name.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 10)
        .map((user) => ({
          kind: 'user' as const,
          id: user.id,
          name: user.name,
          email: user.email,
          title: null,
          photoUrl: null,
          isGuest: user.role === 'guest',
          isDeactivated: user.status !== 'active',
          alreadyOnBoard: board.principals.some((p) => p.kind === 'user' && p.id === user.id),
        })),
    addBoardPrincipals: async ({ userIds }) => {
      for (const id of userIds ?? []) {
        const user = users.find((candidate) => candidate.id === id);
        if (!user) continue;
        board = {
          ...board,
          principals: [
            ...board.principals,
            principal('user', user.id, user.name, 'subscriber', { isGuest: user.role === 'guest' }),
          ],
        };
      }
      return undefined;
    },
    removeBoardPrincipals: async ({ userIds, teamIds }) => {
      board = {
        ...board,
        principals: board.principals.filter(
          (p) => !(p.kind === 'user' && userIds?.includes(p.id)) && !(p.kind === 'team' && teamIds?.includes(p.id)),
        ),
      };
      return undefined;
    },
    setBoardPrincipalRole: async ({ userIds, role }) => {
      board = {
        ...board,
        principals: board.principals.map((p) =>
          p.kind === 'user' && userIds?.includes(p.id) ? { ...p, role } : p,
        ),
      };
      board = { ...board, ownerCount: board.principals.filter((p) => p.role === 'owner').length };
      return undefined;
    },

    workspaceMembers: async () => [
      { id: '2', kind: 'user', name: 'Roberto', title: 'CTO', email: 'roberto@example.com', role: 'owner' },
      { id: '5', kind: 'user', name: 'Fedor', title: null, email: 'fedor@example.com', role: 'member' },
    ],
    addWorkspaceMembers: async () => undefined,
    removeWorkspaceMembers: async () => undefined,
  };
}

function count<T>(rows: T[], key: (row: T) => string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of rows) {
    const value = key(row);
    out[value] = (out[value] ?? 0) + 1;
  }
  return out;
}
